import { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';

// Employee self-service offboarding ("İşten Ayrılışım").
// Mirrors the mobile offboarding screens against the /v1/my-offboarding API.
//
// Load order matters: GetHome returns { Status:true, Data:null } when the user
// has no active offboarding (a legitimate empty state, no toast). The sub-tab
// endpoints (tasks/handover/exit-interview) only return data when an active
// offboarding exists, so we only fetch them once home confirms one. The
// exit-interview endpoint 404s when no interview row exists yet — we pass
// skipErrorToast so that empty state doesn't surface a toast.
export default function useMyOffboardingApi() {
  const [loading, setLoading] = useState(true);
  const [home, setHome] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [handover, setHandover] = useState(null);
  const [interview, setInterview] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    (async () => {
      const homeRes = await api.get('/v1/my-offboarding');
      const homeData = homeRes?.data?.data || null;
      if (cancelled) return;

      setHome(homeData);

      if (!homeData) {
        setTasks([]);
        setHandover(null);
        setInterview(null);
        setLoading(false);
        return;
      }

      const [tasksRes, handoverRes, interviewRes] = await Promise.all([
        api.get('/v1/my-offboarding/tasks'),
        api.get('/v1/my-offboarding/handover'),
        api.get('/v1/my-offboarding/exit-interview', { skipErrorToast: true }),
      ]);
      if (cancelled) return;

      const tasksData = tasksRes?.data?.data;
      const tasksList = Array.isArray(tasksData?.data)
        ? tasksData.data
        : (Array.isArray(tasksData) ? tasksData : []);

      setTasks(tasksList);
      setHandover(handoverRes?.data?.data || null);
      setInterview(interviewRes?.data?.data || null);
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [refreshTrigger]);

  const startTask = useCallback(async (taskId) => {
    try {
      await api.post(`/v1/my-offboarding/tasks/${taskId}/start`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const completeTask = useCallback(async (taskId, notesMarkdown) => {
    try {
      await api.post(`/v1/my-offboarding/tasks/${taskId}/complete`, { notesMarkdown: notesMarkdown || null });
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const saveHandover = useCallback(async (offboardingId, itemsJson) => {
    try {
      await api.post('/v1/my-offboarding/handover', { offboardingId, itemsJson });
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const submitHandover = useCallback(async (offboardingId, itemsJson) => {
    try {
      // Persist the latest items, then transition to review.
      await api.post('/v1/my-offboarding/handover', { offboardingId, itemsJson });
      await api.post('/v1/my-offboarding/handover/submit');
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const acknowledgeHandover = useCallback(async () => {
    try {
      await api.post('/v1/my-offboarding/handover/acknowledge');
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const submitInterview = useCallback(async (interviewId, payload) => {
    try {
      await api.post(`/v1/my-offboarding/exit-interview/${interviewId}/submit`, payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  return {
    loading,
    home,
    tasks,
    handover,
    interview,
    refresh,
    startTask,
    completeTask,
    saveHandover,
    submitHandover,
    acknowledgeHandover,
    submitInterview,
  };
}
